import React from 'react';
import { supabase } from '../lib/supabase';
import KeepsakeList from './keepsakes/KeepsakeList';

export default function CharacterCard({ char, onEdit, keepsakes, onRefresh }) {
  const natures = (char.natures || '').split('\n').filter(Boolean);
  const abilities = (char.abilities || '').split('\n').filter(Boolean);
  const tokens = char.tokens || 0;

  const handleDelete = async () => {
    if (window.confirm('Remove this character?')) {
      await supabase.from('characters').delete().eq('id', char.id);
      onRefresh?.();
    }
  };

  const toggleHide = async () => {
    await supabase.from('characters').update({ is_hidden: !char.is_hidden }).eq('id', char.id);
    onRefresh?.();
  };

  const updateTokens = async (newTokens) => {
    await supabase.from('characters').update({ tokens: newTokens }).eq('id', char.id);
    onRefresh?.();
  };

  return (
    <div className={`bg-parchment-mid border-[1.5px] border-parchment-dark border-l-[3px] border-l-theme-plum rounded-md p-5 pb-4 shadow-card transition-all flex flex-col ${char.is_hidden ? 'hover:opacity-100 hover:saturate-100 opacity-60 saturate-50' : 'hover:-translate-y-[2px] hover:shadow-card-hover'}`}>
      {char.is_hidden && (
        <div className="font-lora text-[0.7rem] uppercase tracking-widest text-ink-light mb-2 font-bold">
          Hidden
        </div>
      )}
      
      <div className="font-playfair text-xl font-bold text-ink mb-0.5">{char.name}</div>
      {char.pronouns && <div className="font-lora text-sm italic text-ink-light mb-3">{char.pronouns}</div>}

      {/* Tokens */}
      <div className="flex items-center gap-2 mb-3">
        <span className="font-lora text-[0.85rem] text-ink-mid min-w-[50px]">Tokens</span>
        <div className="flex gap-1.5 flex-wrap">
          {[0, 1, 2, 3, 4].map(i => (
            <button
              key={i}
              onClick={() => updateTokens(i < tokens ? i : i + 1)}
              className={`w-[18px] h-[18px] rounded-full border-[1.5px] border-theme-honey cursor-pointer transition-transform hover:scale-115 ${i < tokens ? 'bg-theme-honey' : 'bg-transparent'}`}
            />
          ))}
        </div>
        <div className="flex gap-1 ml-1.5">
          <button onClick={() => updateTokens(Math.max(0, tokens - 1))} className="w-[22px] h-[22px] rounded-full border-[1.5px] border-parchment-dark bg-parchment text-[0.9rem] flex items-center justify-center text-ink-mid transition-colors hover:bg-ink hover:text-parchment hover:border-ink leading-none">−</button>
          <button onClick={() => updateTokens(Math.min(5, tokens + 1))} className="w-[22px] h-[22px] rounded-full border-[1.5px] border-parchment-dark bg-parchment text-[0.9rem] flex items-center justify-center text-ink-mid transition-colors hover:bg-ink hover:text-parchment hover:border-ink leading-none">+</button>
        </div>
      </div>

      <div className="flex-grow space-y-3">
        {char.playbook && (
          <div>
            <div className="font-lora text-[0.82rem] text-ink-light tracking-wider uppercase mb-1">Playbook</div>
            <span className="inline-block bg-theme-plum/15 text-theme-plum border border-theme-plum/30 rounded px-2.5 py-0.5 font-playfair text-[0.85rem] italic">
              {char.playbook}
            </span>
          </div>
        )}

        {natures.length > 0 && (
          <div>
            <div className="font-lora text-[0.82rem] text-ink-light tracking-wider uppercase mb-1">Natures & Traits</div>
            <div className="flex flex-wrap gap-1.5">
              {natures.map((n, i) => (
                <span key={i} className="bg-theme-plum/10 text-theme-plum border border-theme-plum/25 rounded-full px-2.5 py-0.5 font-lora text-[0.78rem]">
                  {n.trim()}
                </span>
              ))}
            </div>
          </div>
        )}

        {abilities.length > 0 && (
          <div>
            <div className="font-lora text-[0.82rem] text-ink-light tracking-wider uppercase mb-1">Moves & Abilities</div>
            <div className="flex flex-col gap-1">
              {abilities.map((a, i) => (
                <div key={i} className="font-lora text-[0.82rem] text-ink-mid leading-snug">⬡ {a.trim()}</div>
              ))}
            </div>
          </div>
        )}

        {char.backstory && (
          <div>
            <div className="font-lora text-[0.82rem] text-ink-light tracking-wider uppercase mb-1">Backstory</div>
            <div className="font-lora text-[0.82rem] text-ink-mid leading-relaxed whitespace-pre-wrap">{char.backstory}</div>
          </div>
        )}

        {char.notes && (
          <div>
            <div className="font-lora text-[0.82rem] text-ink-light tracking-wider uppercase mb-1">Notes</div>
            <div className="font-lora text-[0.82rem] text-ink-mid leading-relaxed whitespace-pre-wrap">{char.notes}</div>
          </div>
        )}

        {/* Keepsakes */}
        <KeepsakeList characterId={char.id} keepsakes={keepsakes} onRefresh={onRefresh} />
      </div>

      <div className="flex justify-between items-center pt-3 border-t border-dashed border-parchment-dark mt-3">
        <div className="flex gap-2 items-center">
          <button onClick={onEdit} className="bg-transparent border border-parchment-dark rounded px-3 py-1 font-lora text-[0.76rem] text-ink-mid cursor-pointer transition-colors hover:bg-ink hover:text-parchment hover:border-ink">
            edit
          </button>
          <button onClick={toggleHide} className="bg-transparent border border-parchment-dark rounded px-3 py-1 font-lora text-[0.76rem] text-ink-mid cursor-pointer transition-colors hover:bg-ink hover:text-parchment hover:border-ink">
            {char.is_hidden ? 'unhide' : 'hide'}
          </button>
        </div>
        <button onClick={handleDelete} className="bg-transparent border-none text-theme-roseLight cursor-pointer text-[0.76rem] font-lora opacity-50 transition-opacity hover:opacity-100">
          remove
        </button>
      </div>
    </div>
  );
}